import React, { FC } from 'react';

import { Box, Grid, HStack, Stack, Text } from '@chakra-ui/react';
import { flatMap, isArray, isPlainObject, startCase, uniq } from 'lodash';

import { CustomTable2 as Table } from 'components/DataDisplay/NewTable/Table';
import { TableCellTooltip } from 'components/DataDisplay/NewTable/TableCellTooltip';
import { renderTimeStackedCell } from 'components/DataDisplay/Utils/renderTime';
import { StackedCell } from 'components/DataDisplay/Utils/StackedCell';
import { CheckmarkIcon, CloseIcon } from 'components/Icons';
import { MetaField } from 'containers/ActiveCloudResource/Components/ResourceMetadataDrawer/Components/MetadataField/MetaGridFields';
import { MetaGridValueBox } from 'containers/ActiveCloudResource/Components/ResourceMetadataDrawer/Components/MetadataField/MetaGridValueBox';

import { toBoolean } from './utils';

interface Props {
  label: string;
  data: Record<string, any>[];
}

const objectEntries = value =>
  isPlainObject(value) ? Object.entries(value) : [];

const formatValue = value => {
  if (value === null || value === undefined || value === '') return '-';
  if (isArray(value)) return value.map(formatValue).join(', ');
  if (isPlainObject(value)) return JSON.stringify(value);
  return String(value);
};

const expandableEntries = row =>
  Object.entries(row).filter(([, value]) => objectEntries(value).length > 2);

// Metadata keys use mixed casing, so normalize them before identifying time fields.
const isTimeField = key => {
  const tokens = startCase(key).toLowerCase().split(' ');
  return ['at', 'date', 'time', 'timestamp'].includes(
    tokens[tokens.length - 1],
  );
};

const renderObjectEntry = ([key, value]) => (
  <HStack spacing={1} minW={0}>
    <Text flexShrink={0}>{startCase(key)}:</Text>
    <Text overflow="hidden" textOverflow="ellipsis">
      {formatValue(value)}
    </Text>
  </HStack>
);

const renderCellValue = (key, value) => {
  const entries = objectEntries(value);

  // Keep small objects visible in the cell while preserving each field label.
  if (entries.length === 2) {
    return (
      <StackedCell
        upper={renderObjectEntry(entries[0])}
        lower={renderObjectEntry(entries[1])}
        upperTooltip={`${startCase(entries[0][0])}: ${formatValue(
          entries[0][1],
        )}`}
        lowerTooltip={`${startCase(entries[1][0])}: ${formatValue(
          entries[1][1],
        )}`}
      />
    );
  }

  // Larger objects are rendered in the expandable row to avoid oversized cells.
  if (entries.length > 2) {
    return <Text color="primary">View details</Text>;
  }

  if (isTimeField(key) && !isArray(value)) {
    return renderTimeStackedCell({ value });
  }

  // Flags such as masked / protected read faster as icons than True / False.
  const flag = toBoolean(value);
  if (flag !== undefined) {
    return flag ? (
      <CheckmarkIcon color="green.600" w={3} h={3} aria-label="Yes" />
    ) : (
      <CloseIcon color="gray.400" w={3} h={3} aria-label="No" />
    );
  }

  const formattedValue = formatValue(value);
  return <TableCellTooltip label={formattedValue} value={formattedValue} />;
};

export const DynamicObjectListTable: FC<Props> = ({ label, data }) => {
  // Build a stable union so fields present only in later rows still get a column.
  const keys = uniq(flatMap(data, row => Object.keys(row)));
  const columns = keys.map((key, index) => ({
    header: startCase(key),
    accessor: key,
    align: 'left',
    render: ({ value }) => renderCellValue(key, value),
    // Match the sticky first body cell when the table scrolls horizontally.
    styles:
      index === 0
        ? { headerCell: { position: 'sticky', left: 0, zIndex: 2 } }
        : undefined,
  }));
  const height = Math.min(48 + data.length * 52, 360);

  return (
    <MetaGridValueBox
      label={label}
      value={
        <Box h={`${height}px`} minH="130px" w="full">
          <Table
            columns={columns}
            data={data}
            cursor={row =>
              expandableEntries(row).length ? 'pointer' : 'default'
            }
            subComponent={({ row, expanded }) =>
              expanded &&
              !!expandableEntries(row).length && (
                <Stack spacing={4} py={3}>
                  {expandableEntries(row).map(([key, value]) => (
                    <Stack key={key} spacing={2}>
                      <Text fontWeight={600}>{startCase(key)}</Text>
                      <Grid templateColumns="repeat(4, 1fr)" gap={4}>
                        {objectEntries(value).map(([entryKey, entryValue]) => (
                          <React.Fragment key={entryKey}>
                            {MetaField(
                              startCase(entryKey),
                              isTimeField(entryKey)
                                ? renderTimeStackedCell({ value: entryValue })
                                : formatValue(entryValue),
                              isTimeField(entryKey) ? { custom: true } : {},
                            )}
                          </React.Fragment>
                        ))}
                      </Grid>
                    </Stack>
                  ))}
                </Stack>
              )
            }
          />
        </Box>
      }
    />
  );
};
